import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, MapPin, Video, Phone, Link as LinkIcon, ChevronLeft, Users } from 'lucide-react';
import { schedulingApi } from '@/lib/api';
import { Calendar } from '@/components/scheduling/Calendar';
import { TimeSlots } from '@/components/scheduling/TimeSlots';
import { BookingForm, BookingFormData } from '@/components/scheduling/BookingForm';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

const HOST_CONNECTIVITY_FAILURE_CODES = new Set(['ZOOM_NOT_CONNECTED']);

interface BookingFailureState {
    message: string;
    startTime: string;
    invitee: { name: string; email: string; phone?: string };
}
import {
    isWebinarEventType, upcomingSessions, soleOpenSession, sessionSpotsLabel, isSessionFull,
    parseBookingPrefill, formatEventPrice, redirectToCheckout, type EventSession,
} from '@/lib/scheduling/webinar';
import { friendlyMessage } from '@/lib/errors';
import { normalizeCustomQuestions } from '@/lib/scheduling/customQuestions';
import { isEmbedRequested, withEmbedParam, useEmbedAutoHeight } from '@/lib/scheduling/embed';

interface TimeSlot {
    start_time: string;
    end_time: string;
    available: boolean;
}

const getLocationLabel = (eventType: any) => {
    let locs = eventType?.location_options;
    if (typeof locs === 'string') { try { locs = JSON.parse(locs); } catch { locs = []; } }
    if (!Array.isArray(locs) || locs.length === 0) {
        if (eventType?.location_type === 'google_meet') return { label: 'Google Meet', icon: <Video className="h-4 w-4" /> };
        if (eventType?.location_type === 'zoom') return { label: 'Zoom', icon: <Video className="h-4 w-4" /> };
        if (eventType?.location_type === 'phone') return { label: 'Phone Call', icon: <Phone className="h-4 w-4" /> };
        return { label: 'Google Meet', icon: <Video className="h-4 w-4" /> };
    }
    if (locs.length > 1) return { label: 'Your choice of location', icon: <MapPin className="h-4 w-4" /> };
    const type = locs[0].type;
    if (type === 'google_meet') return { label: 'Google Meet', icon: <Video className="h-4 w-4" /> };
    if (type === 'zoom') return { label: 'Zoom', icon: <Video className="h-4 w-4" /> };
    if (type === 'phone') return { label: 'Phone Call', icon: <Phone className="h-4 w-4" /> };
    if (type === 'in_person') return { label: locs[0].details || 'In Person', icon: <MapPin className="h-4 w-4" /> };
    if (type === 'custom') return { label: locs[0].details || 'Custom Link', icon: <LinkIcon className="h-4 w-4" /> };
    return { label: type, icon: <MapPin className="h-4 w-4" /> };
};

export default function BookEvent() {
    const { slug, orgSlug } = useParams<{ slug: string; orgSlug?: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [eventType, setEventType] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>();
    const [selectedSlot, setSelectedSlot] = useState<TimeSlot | undefined>();
    const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [bookingFailure, setBookingFailure] = useState<BookingFailureState | null>(null);
    const [notifyStatus, setNotifyStatus] = useState<'idle' | 'sending' | 'sent' | 'already_reported' | 'resolved' | 'error'>('idle');
    const notifiedEmailsRef = useRef<Set<string>>(new Set());
    const [step, setStep] = useState<'date' | 'time' | 'session' | 'details'>('date');
    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [loadingDates, setLoadingDates] = useState(false);

    const [selectedSession, setSelectedSession] = useState<EventSession | undefined>();
    const [prefill] = useState(() => parseBookingPrefill(window.location.search));

    const isWebinar = isWebinarEventType(eventType);
    const sessions = isWebinar ? upcomingSessions(eventType?.sessions) : [];
    // One open session → the form opens directly, so there is no list to go back to.
    const isSoleSession = isWebinar && soleOpenSession(eventType?.sessions) !== null;

    // Opened by scheduler-widget.js (`?embed=1`): the widget already draws the
    // box, so a webinar page renders just its form — no page background, no
    // card border/shadow — and reports its height so the box fits the form.
    // Webinars only; slot booking pages keep their layout.
    const [embedRequested] = useState(() => isEmbedRequested(window.location.search));
    const embedded = embedRequested && isWebinar;
    const [embedRoot, setEmbedRoot] = useState<HTMLDivElement | null>(null);
    useEmbedAutoHeight(embedRoot, embedded);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const response = orgSlug
                    ? await schedulingApi.getEventTypeByOrgAndSlug(orgSlug, slug!)
                    : await schedulingApi.getEventTypeBySlug(slug!);
                setEventType(response.data);
                if (isWebinarEventType(response.data)) {
                    const sole = soleOpenSession(response.data?.sessions);
                    if (sole) {
                        setSelectedSession(sole);
                        setStep('details');
                    } else {
                        setStep('session');
                    }
                }
            } catch {
                toast({ title: 'Error', description: 'Event type not found', variant: 'destructive' });
            } finally {
                setLoading(false);
            }
        };
        if (slug) load();
    }, [slug, orgSlug]);

    useEffect(() => {
        if (!eventType || isWebinarEventType(eventType)) return;
        const load = async () => {
            try {
                setLoadingDates(true);
                const today = new Date();
                const future = new Date();
                future.setDate(today.getDate() + 60);
                const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                const response = await schedulingApi.getAvailableSlotsRange(eventType.id, fmt(today), fmt(future));
                const dates: string[] = [];
                Object.entries(response.data || {}).forEach(([date, slots]) => {
                    if (Array.isArray(slots) && slots.length > 0) dates.push(date);
                });
                setAvailableDates(dates);
            } catch {
                setAvailableDates([]);
            } finally {
                setLoadingDates(false);
            }
        };
        load();
    }, [eventType]);

    useEffect(() => {
        if (!selectedDate || !eventType || isWebinarEventType(eventType)) return;
        const load = async () => {
            try {
                setLoadingSlots(true);
                const d = selectedDate;
                const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                const response = await schedulingApi.getAvailableSlotsForDate(eventType.id, dateStr);
                setAvailableSlots(response.data || []);
                setStep('time');
            } catch {
                toast({ title: 'Error', description: 'Failed to load available times', variant: 'destructive' });
                setAvailableSlots([]);
            } finally {
                setLoadingSlots(false);
            }
        };
        load();
    }, [selectedDate, eventType]);

    const handleBack = () => {
        if (step === 'details' && isWebinar) { setStep('session'); setSelectedSession(undefined); }
        else if (step === 'details') { setStep('time'); setSelectedSlot(undefined); }
        else if (step === 'time') { setStep('date'); setSelectedDate(undefined); setAvailableSlots([]); }
    };

    const isDark = eventType?.theme === 'dark';
    useEffect(() => {
        if (!isDark) {
            document.documentElement.classList.remove('dark');
            return;
        }
        const wasDark = document.documentElement.classList.contains('dark');
        document.documentElement.classList.add('dark');
        return () => {
            if (!wasDark) document.documentElement.classList.remove('dark');
        };
    }, [isDark]);

    const handleBookingSubmit = async (formData: BookingFormData) => {
        if (!eventType) return;
        if (isWebinar ? !selectedSession : !selectedSlot) return;
        try {
            setSubmitting(true);
            const bookingData = {
                event_type_id: eventType.id,
                ...(isWebinar ? { session_id: selectedSession!.id } : {}),
                start_time: isWebinar ? String(new Date(selectedSession!.start_time).toISOString()) : selectedSlot!.start_time,
                end_time: isWebinar ? String(new Date(selectedSession!.end_time).toISOString()) : selectedSlot!.end_time,
                invitee_name: formData.invitee_name,
                invitee_email: formData.invitee_email,
                invitee_phone: formData.invitee_phone,
                guest_emails: formData.guest_emails || [],
                location_type: formData.location_type,
                location_details: formData.location_details,
                notes: formData.notes,
                answers: formData.answers || [],
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                whatsapp_reminder_enabled: !!eventType.whatsapp_reminders_enabled,
            };
            const response = await schedulingApi.createBooking(bookingData);
            if (response?.success === false || !response?.data?.id) {
                const code = (response as any)?.code;
                if (code && HOST_CONNECTIVITY_FAILURE_CODES.has(code)) {
                    const emailKey = formData.invitee_email.trim().toLowerCase();
                    setNotifyStatus(notifiedEmailsRef.current.has(emailKey) ? 'already_reported' : 'idle');
                    setBookingFailure({
                        message: friendlyMessage(response?.message || response?.error, "We couldn't complete this booking. Please try again."),
                        startTime: bookingData.start_time,
                        invitee: {
                            name: formData.invitee_name,
                            email: formData.invitee_email,
                            phone: formData.invitee_phone,
                        },
                    });
                    return;
                }
                toast({
                    title: 'Booking Failed',
                    description: response?.message || response?.error || 'Failed to create booking',
                    variant: 'destructive',
                });
                return;
            }
            if (response.data.payment_required && response.data.payment_url) {
                redirectToCheckout(response.data.payment_url);
                return;
            }
            navigate(withEmbedParam(`/booking-success/${response.data.id}`, embedded));
        } catch (error: any) {
            toast({ title: 'Booking Failed', description: error.response?.data?.error || 'Failed to create booking', variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleNotifyHost = async () => {
        if (!bookingFailure || !eventType) return;
        setNotifyStatus('sending');
        try {
            const res = await schedulingApi.notifyBookingFailure({
                event_type_id: eventType.id,
                requested_start_time: bookingFailure.startTime,
                invitee_email: bookingFailure.invitee.email,
                invitee_name: bookingFailure.invitee.name,
                invitee_phone: bookingFailure.invitee.phone,
            });
            if (res?.success === false) {
                setNotifyStatus('error');
                return;
            }
            const status = (res as any)?.data?.status;
            if (status === 'already_reported') {
                setNotifyStatus('already_reported');
                notifiedEmailsRef.current.add(bookingFailure.invitee.email.trim().toLowerCase());
            } else if (status === 'resolved') {
                setNotifyStatus('resolved');
            } else {
                setNotifyStatus('sent');
                notifiedEmailsRef.current.add(bookingFailure.invitee.email.trim().toLowerCase());
            }
        } catch {
            setNotifyStatus('error');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
            </div>
        );
    }

    if (!eventType) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-2">Event Not Found</h2>
                    <p className="text-muted-foreground">This event doesn't exist or has been removed.</p>
                </div>
            </div>
        );
    }

    const isEventPaused = eventType.active === false || eventType.active === 0 || eventType.active === '0';
    if (isEventPaused) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center max-w-md">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                        <CalendarIcon className="h-6 w-6 text-amber-600" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">Not Accepting Bookings</h2>
                    <p className="text-muted-foreground">
                        "{(eventType.name || '').replace(/[_-]+/g, ' ').trim()}" is currently paused and not
                        accepting new bookings. Please contact the host or check back later.
                    </p>
                </div>
            </div>
        );
    }

    const location = getLocationLabel(eventType);

    const mutedText = isDark ? 'text-slate-400' : 'text-muted-foreground';
    const borderColor = isDark ? 'border-slate-700' : '';

    const InfoPanel = () => (
        <div className="space-y-5">
            <div className="flex items-center gap-3">
                <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: eventType.color || '#10b981' }}
                >
                    <CalendarIcon className="h-5 w-5 text-white" />
                </div>
                <span className={`text-sm font-medium ${mutedText}`}>{eventType.host_name || 'Host'}</span>
            </div>

            <div className="min-w-0">
                <h1 className={`text-xl font-semibold leading-tight break-words capitalize ${isDark ? 'text-white' : ''}`}>
                    {(eventType.name || '').replace(/[_-]+/g, ' ').trim()}
                </h1>
                {eventType.description && (
                    <p className={`text-sm mt-2 leading-relaxed break-words ${mutedText}`}>{eventType.description}</p>
                )}
            </div>

            <div className={`border-t pt-4 space-y-3 ${borderColor}`}>
                {isWebinar && selectedSession && (
                    <div className="flex items-start gap-2 text-sm">
                        <CalendarIcon className={`h-4 w-4 mt-0.5 shrink-0 ${mutedText}`} />
                        <div>
                            <p className={`font-medium ${isDark ? 'text-slate-200' : ''}`}>
                                {new Date(selectedSession.start_time).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                            </p>
                            <p className={mutedText}>
                                {new Date(selectedSession.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                {' – '}
                                {new Date(selectedSession.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </p>
                        </div>
                    </div>
                )}

                {!isWebinar && selectedDate && (
                    <div className="flex items-start gap-2 text-sm">
                        <CalendarIcon className={`h-4 w-4 mt-0.5 shrink-0 ${mutedText}`} />
                        <div>
                            <p className={`font-medium ${isDark ? 'text-slate-200' : ''}`}>
                                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                            </p>
                            {selectedSlot && (
                                <p className={mutedText}>
                                    {new Date(selectedSlot.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    {' – '}
                                    {new Date(selectedSlot.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                <div className={`flex items-center gap-2 text-sm ${mutedText}`}>
                    <Clock className="h-4 w-4 shrink-0" />
                    <span>{eventType.duration} minutes</span>
                </div>

                {formatEventPrice(eventType.price, eventType.currency) && (
                    <div className={`flex items-center gap-2 text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        <span className="text-base">💳</span>
                        <span>{formatEventPrice(eventType.price, eventType.currency)} per registration</span>
                    </div>
                )}

                <div className={`flex items-center gap-2 text-sm ${mutedText}`}>
                    <span className="shrink-0">{location.icon}</span>
                    <span>{location.label}</span>
                </div>

                <div className={`flex items-center gap-2 text-sm ${mutedText}`}>
                    <span className="text-base">🌐</span>
                    <span>{Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
                </div>
            </div>
        </div>
    );

    return (
        <div
            ref={setEmbedRoot}
            className={embedded
                ? (isDark ? 'dark bg-[#111827] text-slate-100' : 'bg-background')
                : `min-h-screen flex items-center justify-center py-8 px-4 ${isDark ? 'dark bg-[#0b1120]' : 'bg-muted/20'}`}
            data-testid={embedded ? 'book-event-embedded' : undefined}
        >
            <div className={`w-full ${embedded ? '' : isWebinar ? 'max-w-[560px]' : 'max-w-[780px]'}`}>
                <div className={embedded
                    ? ''
                    : `rounded-2xl border shadow-sm overflow-hidden ${isDark ? 'bg-[#111827] border-[#1e293b] text-slate-100' : 'bg-background'}`}>
                    {/* Webinars show only the registration content — no event info side panel. */}
                    <div className={`grid grid-cols-1 ${isWebinar ? '' : 'sm:grid-cols-[260px_1fr]'}`}>

                        {!isWebinar && (
                            <div className={`p-6 sm:p-8 border-b sm:border-b-0 sm:border-r min-w-0 ${isDark ? 'bg-[#0f172a] border-[#1e293b]' : 'bg-muted/10'}`}>
                                <InfoPanel />
                            </div>
                        )}

                        <div className="p-6 sm:p-8">
                            {step !== 'date' && step !== 'session' && !(step === 'details' && isSoleSession) && (
                                <button
                                    onClick={handleBack}
                                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    Back
                                </button>
                            )}

                            {/* Webinar form step: no "Register for …" heading — the form starts directly. */}
                            {!(isWebinar && step === 'details' && !bookingFailure) && (
                            <div className="mb-6">
                                <h2 className="text-base font-semibold text-foreground">
                                    {step === 'date' && 'Select a Date'}
                                    {step === 'time' && `Available Times — ${selectedDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
                                    {step === 'session' && 'Select a Session'}
                                    {step === 'details' && (
                                        bookingFailure
                                            ? 'Booking status'
                                            : isWebinar && selectedSession
                                                ? `Register for ${(eventType.name || '').replace(/[_-]+/g, ' ').trim()}`
                                                : 'Enter your details'
                                    )}
                                </h2>
                                {step === 'date' && (
                                    <p className="text-sm text-muted-foreground mt-0.5">Choose a date to see available times</p>
                                )}
                                {step === 'session' && (
                                    <p className="text-sm text-muted-foreground mt-0.5">Pick the session you want to register for</p>
                                )}
                                {step === 'details' && isWebinar && selectedSession && (
                                    <p className="text-sm text-muted-foreground mt-0.5">
                                        {new Date(selectedSession.start_time).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                        {' · '}
                                        {new Date(selectedSession.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                        {' – '}
                                        {new Date(selectedSession.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    </p>
                                )}
                            </div>
                            )}

                            {step === 'session' && (
                                sessions.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground" data-testid="webinar-no-sessions">
                                        <CalendarIcon className="h-8 w-8" />
                                        <p className="text-sm font-medium">No upcoming sessions</p>
                                        <p className="text-xs">Please check back later or contact the host.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2.5" data-testid="webinar-session-list">
                                        {sessions.map((session) => {
                                            const full = isSessionFull(session);
                                            const spots = sessionSpotsLabel(session);
                                            const start = new Date(session.start_time);
                                            const end = new Date(session.end_time);
                                            return (
                                                <button
                                                    key={session.id}
                                                    type="button"
                                                    disabled={full}
                                                    onClick={() => { setSelectedSession(session); setStep('details'); }}
                                                    className={`w-full flex items-center justify-between gap-3 p-4 border rounded-lg text-left transition-colors ${full
                                                        ? 'opacity-50 cursor-not-allowed bg-muted/20'
                                                        : 'hover:border-primary hover:bg-muted/40'}`}
                                                    data-testid="webinar-session-option"
                                                >
                                                    <div className="min-w-0">
                                                        <p className={`text-sm font-semibold ${isDark ? 'text-slate-100' : 'text-foreground'}`}>
                                                            {start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                                        </p>
                                                        <p className={`text-sm ${mutedText}`}>
                                                            {start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                                            {' – '}
                                                            {end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {spots && (
                                                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${full
                                                                ? 'bg-red-50 text-red-600'
                                                                : 'bg-emerald-50 text-emerald-600'}`}>
                                                                <Users className="h-3 w-3" />
                                                                {spots}
                                                            </span>
                                                        )}
                                                        {!full && <ChevronLeft className="h-4 w-4 rotate-180 text-muted-foreground" />}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )
                            )}

                            {step === 'date' && (
                                loadingDates ? (
                                    <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                                        <span className="text-sm">Loading availability...</span>
                                    </div>
                                ) : (
                                    <Calendar
                                        onDateSelect={setSelectedDate}
                                        selectedDate={selectedDate}
                                        minDate={new Date()}
                                        availableDates={availableDates}
                                    />
                                )
                            )}

                            {step === 'time' && (
                                <TimeSlots
                                    slots={availableSlots}
                                    selectedSlot={selectedSlot}
                                    onSlotSelect={(slot) => { setSelectedSlot(slot); setStep('details'); }}
                                    loading={loadingSlots}
                                    timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                                />
                            )}

                            {step === 'details' && (
                                bookingFailure ? (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/60 p-5 space-y-4">
                                        <div>
                                            <h3 className="font-semibold text-amber-900 dark:text-amber-200 mb-1">Booking unavailable</h3>
                                            <p className="text-sm text-amber-800 dark:text-amber-300">{bookingFailure.message}</p>
                                        </div>

                                        {notifyStatus === 'idle' && (
                                            <Button onClick={handleNotifyHost} className="w-full">Notify host</Button>
                                        )}
                                        {notifyStatus === 'sending' && (
                                            <p className="text-sm text-muted-foreground">Notifying host…</p>
                                        )}
                                        {notifyStatus === 'sent' && (
                                            <p className="text-sm text-emerald-700 dark:text-emerald-400">
                                                Host notified ✓ — they've been alerted and will follow up. You can try booking again later.
                                            </p>
                                        )}
                                        {notifyStatus === 'already_reported' && (
                                            <p className="text-sm text-muted-foreground">
                                                We've already let the host know — they'll follow up with you soon. You can try booking again later.
                                            </p>
                                        )}
                                        {notifyStatus === 'resolved' && (
                                            <p className="text-sm text-emerald-700 dark:text-emerald-400">
                                                Good news — this looks fixed now. Please try booking again.
                                            </p>
                                        )}
                                        {notifyStatus === 'error' && (
                                            <p className="text-sm text-destructive">
                                                Something went wrong sending the notification. Please try again.
                                            </p>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => { setBookingFailure(null); setNotifyStatus('idle'); }}
                                            className="text-sm underline text-muted-foreground hover:text-foreground"
                                        >
                                            Try booking again
                                        </button>
                                    </div>
                                ) : (
                                    <BookingForm
                                        onSubmit={handleBookingSubmit}
                                        loading={submitting}
                                        eventType={eventType}
                                        prefill={prefill}
                                        showWhatsAppPhone={!!eventType.whatsapp_reminders_enabled}
                                        customQuestions={normalizeCustomQuestions(eventType.custom_questions)}
                                    />
                                )
                            )}
                        </div>
                    </div>
                </div>

                {!embedded && (
                    <p className="text-center text-xs text-muted-foreground mt-6">
                        © 2025 WeNext. Powered by PhotonX
                    </p>
                )}
            </div>
        </div>
    );
}
